import { useCallback, useEffect, useId, useReducer, useRef } from "react";
import type { AIActionApprovalResponse } from "@k8s-dashboard/contracts";
import { useAnnounce } from "@/app/Announcer";
import { useT } from "@/i18n/I18nProvider";
import { approvalNonce, confirmApproval, idempotencyKey, startDryRun } from "@/api/aiActions";
import { ApprovalDialog } from "./ApprovalDialog";
import { INITIAL_APPROVAL, approvalReducer, currentReview, dialogOpen, isBusy } from "./approvalState";

/**
 * 승인 컨트롤 — ADR 0028 Phase C · Issue #20
 * --------------------------------------------------------------------------
 * **두 번 누르지 않으면 아무 일도 일어나지 않습니다.** 첫 번째는 검토를 열고,
 * 두 번째는 그 검토를 확정합니다. 한 번에 실행되는 경로는 이 파일에 없습니다.
 *
 * 다섯 가지가 이 컴포넌트의 형태를 정합니다.
 *
 *   게이트    `recordId`가 없으면 아무것도 그리지 않습니다. 그 값은 서버 신호 **둘**
 *            (이 턴의 승인 참조 · 이 배포·사용자의 능력)이 모두 참일 때만 전달되며,
 *            판정은 ActionCardList 한 곳에 있습니다 — 두 곳이면 언젠가 갈라집니다.
 *   잠금      진행 중에는 버튼이 비활성입니다. 상태 기계가 시작 이벤트 자체를
 *            무시하므로 더블 클릭이 두 번째 요청이 되지 않습니다.
 *   멱등      재시도는 **같은 키**로 갑니다. 키를 새로 만들면 재시도가 두 번째
 *            실행이 됩니다. 새 검토를 열 때만 새 키를 만듭니다.
 *   취소      화면을 떠나면 요청이 취소됩니다. 확정은 서버가 제출 직전까지만
 *            취소를 받아들이고, 그 뒤는 결과가 기록됩니다.
 *   낭독      상태 요약만 공용 Announcer로 갑니다. 서버·Kubernetes·모델 문자열은
 *            live region에 들어가지 않습니다.
 */
export function ActionApproval({ recordId }: { recordId?: string }) {
  const { t } = useT("approve");
  const announce = useAnnounce();
  const [state, dispatch] = useReducer(approvalReducer, INITIAL_APPROVAL);
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  /* 요청 하나가 살아 있는 동안만 유효한 취소기입니다. */
  const abortRef = useRef<AbortController | null>(null);
  /* 재시도가 같은 요청이 되도록 단계별 nonce를 보관합니다. */
  const nonces = useRef<{ review: string; confirm: string }>({ review: "", confirm: "" });
  /* 낭독 key는 이 카드 하나입니다. 같은 카드의 연속된 상태 변화는 한 덩어리로
     접히고, 다른 카드의 알림을 덮지 않습니다. */
  const announceKey = `agent-approval:${recordId ?? "none"}`;

  useEffect(() => () => abortRef.current?.abort(), []);

  const run = useCallback(
    async (task: (signal: AbortSignal) => ReturnType<typeof startDryRun>) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const outcome = await task(controller.signal);
        if (controller.signal.aborted) return;
        if (outcome.status === "error") {
          dispatch({ type: "failed", code: outcome.code });
          /* key가 같으면 Announcer가 중복을 접습니다 — 재시도 실패가 같은 문장을
             여러 번 낭독하지 않습니다. */
          announce(announceKey, t("approve.announce.error"));
          return;
        }
        dispatch({ type: "resolved", response: outcome.response });
        announce(announceKey, announcementFor(t, outcome.response));
      } catch {
        // 취소는 실패가 아닙니다 — 사용자가 떠난 것이고 남길 상태가 없습니다.
        if (!controller.signal.aborted) dispatch({ type: "failed", code: "action_unavailable" });
      }
    },
    [announce, announceKey, t],
  );

  const openReview = useCallback(() => {
    if (!recordId || isBusy(state)) return;
    // 새 검토는 새 요청입니다. 확정 키는 검토가 바뀌면 무효이므로 함께 비웁니다.
    nonces.current = { review: approvalNonce(), confirm: "" };
    dispatch({ type: "review:start" });
    void run((signal) => startDryRun(recordId, idempotencyKey(recordId, "DRY_RUN", nonces.current.review), signal));
  }, [recordId, run, state]);

  const confirm = useCallback(
    (review: AIActionApprovalResponse) => {
      if (!recordId || isBusy(state) || !review.review) return;
      // 확정 키는 **이 확정 시도**의 것입니다. 재시도는 같은 키로 갑니다.
      if (!nonces.current.confirm) nonces.current.confirm = approvalNonce();
      dispatch({ type: "confirm:start" });
      const key = idempotencyKey(recordId, "CONFIRM", nonces.current.confirm);
      void run((signal) => confirmApproval(recordId, review.executionId, review.review!.dryRunDigest, key, signal));
    },
    [recordId, run, state],
  );

  const close = useCallback(() => {
    if (isBusy(state)) return;
    abortRef.current?.abort();
    dispatch({ type: "dismiss" });
    // 닫으면 포커스가 열었던 자리로 돌아갑니다 — 목록 처음으로 튀지 않습니다.
    triggerRef.current?.focus();
  }, [state]);

  const retry = useCallback(() => {
    if (state.kind !== "error") return;
    if (state.review) {
      dispatch({ type: "retry" });
      return;
    }
    dispatch({ type: "retry" });
    openReview();
  }, [openReview, state]);

  if (!recordId) return null;
  const review = currentReview(state);
  return (
    <div className="ds-action-card__approval" data-testid="agent-action-approval">
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--warning ds-action-card__approve"
        data-testid="agent-action-approve"
        disabled={isBusy(state)}
        aria-busy={state.kind === "reviewing"}
        onClick={openReview}
      >
        {state.kind === "reviewing" ? t("approve.reviewing") : t("approve.open")}
      </button>
      {dialogOpen(state) && (
        <ApprovalDialog
          titleId={titleId}
          state={state}
          review={review}
          onConfirm={() => review && confirm(review)}
          onRetry={retry}
          onClose={close}
        />
      )}
    </div>
  );
}

/**
 * 낭독 문구. **상태 요약뿐입니다** — 응답의 어떤 값도 여기 들어가지 않습니다.
 * 값이 들어가는 순간 live region이 서버·클러스터 문자열의 출구가 됩니다.
 */
function announcementFor(t: (id: string) => string, response: AIActionApprovalResponse): string {
  switch (response.status) {
    case "DRY_RUN_READY":
      return t("approve.announce.review");
    case "EXECUTED":
      return t("approve.announce.executed");
    case "UNKNOWN":
      return t("approve.announce.unknown");
    case "FAILED":
    case "REJECTED":
      return t("approve.announce.failed");
    default:
      return t("approve.announce.review");
  }
}
